/**
 * Modale « Valeur d'effectif » au CLIC sur la valeur d'une équipe (classement Elo) :
 * la liste des joueurs les plus chers (top 23 Transfermarkt), leur valeur, et le
 * LIEN vers leur fiche Transfermarkt (la SOURCE de la valeur). Données committées
 * (snapshot `src/data/squad-players.json`, généré par `npm run build:model-data
 * -- --players`). Réutilise la coquille `.md-overlay`.
 */
import squadDoc from "../data/squad-players.json";
import { teamName, flagCode, esc, frontZ } from "./html.ts";
import { track } from "../app/track.ts";
import { t } from "../app/i18n.ts";

interface SquadPlayerVal {
  readonly id: number;
  readonly name: string;
  readonly pos?: string;
  readonly club?: string;
  readonly value: number;
}
// Graine committée (fallback, règle 8) → remplacée par les valeurs FRAÎCHES du
// serveur (/data/squad-players.json, rafraîchies chaque jour) si disponibles.
let PLAYERS = (squadDoc as { players?: Record<string, SquadPlayerVal[]> }).players ?? {};
let GENERATED_AT = (squadDoc as { generatedAt?: string }).generatedAt ?? "";
let fetched = false;
function loadFresh(): void {
  if (fetched) return;
  fetched = true;
  void fetch("/data/squad-players.json", { cache: "no-store" })
    .then((r) =>
      r.ok
        ? (r.json() as Promise<{
            players?: Record<string, SquadPlayerVal[]>;
            generatedAt?: string;
          }>)
        : null,
    )
    .then((d) => {
      // seuil ≥ 30 nations : ne pas adopter un payload dégradé (aligné valueModel).
      if (d?.players && Object.keys(d.players).length >= 30) {
        PLAYERS = d.players;
        if (d.generatedAt) GENERATED_AT = d.generatedAt;
      }
    })
    .catch(() => {});
}

const fmtVal = (v: number): string =>
  v >= 1e6
    ? `${(v / 1e6).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} M€`
    : v > 0
      ? `${Math.round(v / 1e3).toLocaleString("fr-FR")} k€`
      : "—";
const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};
/** Le player_id EST l'identifiant Transfermarkt → fiche profil (où vit la valeur). */
const tmUrl = (id: number): string => `https://www.transfermarkt.fr/-/profil/spieler/${id}`;

function bodyHtml(code: string): string {
  const list = PLAYERS[code] ?? [];
  const total = list.reduce((a, p) => a + p.value, 0);
  const head = `<div class="sv-head">
      <span class="f fi fi-${esc(flagCode(code))} sv-flag" aria-hidden="true"></span>
      <div class="sv-id">
        <div class="sv-name">${esc(teamName(code))}</div>
        <div class="sv-sub">${t("fiche.squadValue.subtitle")} · <b>${fmtVal(total)}</b> · ${t("common.top")} ${list.length}</div>
      </div>
    </div>`;
  if (!list.length) {
    return `${head}<p class="sv-empty note">${t("fiche.squadValue.empty")}</p>`;
  }
  const rows = list
    .map((p, i) => {
      const meta = [p.pos, p.club].filter(Boolean).join(" · ");
      return `<li class="sv-row">
      <span class="sv-rank">${i + 1}</span>
      <div class="sv-pl">
        <span class="sv-pl-name">${esc(p.name || "—")}</span>
        ${meta ? `<span class="sv-pl-meta">${esc(meta)}</span>` : ""}
      </div>
      <span class="sv-val">${fmtVal(p.value)}</span>
      <a class="sv-link" href="${tmUrl(p.id)}" target="_blank" rel="noopener noreferrer" title="${t("fiche.squadValue.sourceTitle")}" aria-label="${t("fiche.squadValue.viewOn", { name: esc(p.name) })}"><i class="ti ti-external-link" aria-hidden="true"></i></a>
    </li>`;
    })
    .join("");
  return `${head}
    <ol class="sv-list">${rows}</ol>
    <p class="note sv-foot">${t("fiche.squadValue.footPrefix")}${GENERATED_AT ? ` ${t("common.asOf")} ${esc(fmtDate(GENERATED_AT))}` : ""} · ${t("fiche.squadValue.footSum", { n: list.length })} ${t("fiche.squadValue.footClickSource", { icon: `<i class="ti ti-external-link" aria-hidden="true"></i>` })}</p>`;
}

// --- contrôleur overlay (réutilise .md-overlay) ----------------------------
let overlay: HTMLElement | null = null;
let open = false;
let installed = false;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  const ov = document.createElement("div");
  ov.className = "md-overlay sv-overlay hide";
  ov.innerHTML = `<div class="md-card sv-card" role="dialog" aria-modal="true" aria-label="${t("fiche.squadValue.subtitle")}"><button type="button" class="md-close" aria-label="${t("common.close")}"><i class="ti ti-x" aria-hidden="true"></i></button><div class="md-body sv-body"></div></div>`;
  ov.addEventListener("click", (e) => {
    if (e.target === ov) close();
  });
  ov.querySelector(".md-close")?.addEventListener("click", () => close());
  overlay = ov;
  document.body.appendChild(ov);
  return ov;
}

function openSquadValue(code: string): void {
  if (!PLAYERS[code]) return;
  track("fiche", { kind: "value", code });
  const ov = ensureOverlay();
  const body = ov.querySelector<HTMLElement>(".sv-body");
  if (!body) return;
  body.innerHTML = bodyHtml(code);
  body.scrollTop = 0;
  ov.classList.remove("hide");
  ov.style.zIndex = String(frontZ()); // au-dessus de toute modale déjà ouverte
  open = true;
  requestAnimationFrame(() => ov.classList.add("open"));
}

function close(): void {
  if (!overlay || !open) return;
  open = false;
  overlay.classList.remove("open");
  const ov = overlay;
  setTimeout(() => {
    if (!open) ov.classList.add("hide");
  }, 300);
}

/** Installe le contrôleur (idempotent) : clic sur `[data-value-team]` → modale. */
export function registerSquadValue(): void {
  if (installed) return;
  installed = true;
  loadFresh(); // valeurs joueurs du jour (sinon graine committée)
  document.addEventListener("click", (e) => {
    const btn =
      e.target instanceof Element ? e.target.closest<HTMLElement>("[data-value-team]") : null;
    const code = btn?.dataset.valueTeam;
    if (!code) return;
    e.preventDefault();
    openSquadValue(code);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") close();
  });
}
